import React, { ChangeEvent, useEffect, useMemo, useState } from 'react'
import {
  CButton,
  CCol,
  CFormCheck,
  CFormInput,
  CFormSelect,
  CFormTextarea,
  CRow,
  CSpinner,
} from '@coreui/react-pro'
import { FormData } from './types'
import {
  $changedAmountData,
  $error,
  $updateAmountRequestSuccess,
  calculateAmount,
  calculateAmountMounted,
  setChangedAmountData,
  setUpdateAmountRequestSuccess,
  updateAmount,
  updateAmountMounted,
} from './model'
import { useUnit } from 'effector-react'
import { NotificationPlacement } from 'antd/es/notification/interface'
import { notification, Tooltip } from 'antd'
import { humanTransactionRequestStatus } from '../../../../const'
import { currencies } from '../../../../../../libs/currencies'
import { InitialValuesForm } from './initialValuesForm'
import { InfoCircleOutlined } from '@ant-design/icons'

type EditableData = {
  initialAmount: string
  clientAmount: string
  clientCurrency: string
  companyAmount: string
  companyCurrency: string
  innerCommission: string
}

const editableFieldsByStatus: Record<string, Array<keyof EditableData>> = {
  TRANSACTION_REQUEST_STATUS_PENDING_MANAGER: [
    'clientAmount',
    'companyAmount',
    'initialAmount',
    'innerCommission',
    'clientCurrency',
    'companyCurrency',
  ],
  TRANSACTION_REQUEST_STATUS_ACCEPTED: [
    'clientAmount',
    'companyAmount',
    'innerCommission',
    'clientCurrency',
    'companyCurrency',
  ],
}

const changeMethod = {
  calculation: false,
  update: true,
}

type Props = {
  initialData: FormData
  transactionStatus: string
  onClose: () => void
}

export const ChangeAmountForm: React.FC<Props> = ({
  initialData,
  transactionStatus,
  onClose,
}) => {
  const [editableData, setEditableData] = useState<EditableData>({
    initialAmount: initialData.initialAmount,
    clientAmount: initialData.clientAmount,
    clientCurrency: initialData.clientCurrency,
    companyAmount: initialData.companyAmount,
    companyCurrency: initialData.companyCurrency,
    innerCommission: initialData.innerCommission,
  })
  const [comment, setComment] = useState<string>('')
  const [needRecalculate, setNeedRecalculate] = useState<boolean>(false)
  const [activeField, setActiveField] = useState<keyof EditableData | null>(
    null,
  )
  const [isFieldModified, setIsFieldModified] = useState<boolean>(false)

  const [api, contextHolder] = notification.useNotification()

  const changedAmountData = useUnit($changedAmountData)
  const calculateAmountLoading = useUnit(calculateAmount.pending)
  const changedAmountError = useUnit($error)

  const updateAmountLoading = useUnit(updateAmount.pending)
  const updateAmountRequestSuccess = useUnit($updateAmountRequestSuccess)

  const openNotification = (placement: NotificationPlacement) => {
    api.error({
      message: `Error`,
      description: `${changedAmountError.message}`,
      placement,
    })
  }

  // Сравниваем текущее значение с оригинальным при изменении активного поля
  useEffect(() => {
    if (activeField) {
      const originalValue = initialData[activeField]
      const editedValue = editableData[activeField]

      setIsFieldModified(originalValue !== editedValue)
    } else {
      setIsFieldModified(false)
    }
  }, [editableData, activeField, initialData])

  // нужен для того, чтобы при изменении значения clientCurrency, изменялось значение companyCurrency
  useEffect(() => {
    if (activeField === 'clientCurrency') {
      setEditableData((prev) => ({
        ...prev,
        companyCurrency: editableData.clientCurrency,
      }))
    }
  }, [editableData.clientCurrency])

  useEffect(() => {
    if (changedAmountData) {
      setEditableData({
        initialAmount: changedAmountData.initialAmount,
        clientAmount: changedAmountData.clientAmount,
        clientCurrency: changedAmountData.clientCurrency,
        companyAmount: changedAmountData.companyAmount,
        companyCurrency: changedAmountData.companyCurrency,
        innerCommission: changedAmountData.innerCommission,
      })
    }
  }, [changedAmountData])

  useEffect(() => {
    if (changedAmountError.code !== 0) {
      openNotification('topRight')
    }
  }, [changedAmountError])

  useEffect(() => {
    if (updateAmountRequestSuccess) {
      onClose()
      setUpdateAmountRequestSuccess(false)
    }
  }, [updateAmountRequestSuccess])

  // Определяет, если хотя бы одно поле изменилось после пересчета
  const isDiffAmountAfterChanged = useMemo(() => {
    if (changedAmountData) {
      const {
        clientAmount,
        initialAmount,
        companyAmount,
        innerCommission,
        clientCommission,
        companyCommission,
        initialAmountUsd,
      } = changedAmountData

      return (
        Number(clientAmount) !== Number(initialData.clientAmount) ||
        Number(initialAmount) !== Number(initialData.initialAmount) ||
        Number(companyAmount) !== Number(initialData.companyAmount) ||
        Number(innerCommission) !== Number(initialData.innerCommission) ||
        Number(clientCommission) !== Number(initialData.clientCommission) ||
        Number(companyCommission) !== Number(initialData.companyCommission) ||
        Number(initialAmountUsd) !== Number(initialData.initialAmountUsd)
      )
    }
    return false
  }, [changedAmountData])

  const handleReset = (): void => {
    setEditableData({
      initialAmount: initialData.initialAmount,
      clientAmount: initialData.clientAmount,
      clientCurrency: initialData.clientCurrency,
      companyAmount: initialData.companyAmount,
      companyCurrency: initialData.companyCurrency,
      innerCommission: initialData.innerCommission,
    })
    setComment('')
    setActiveField(null)
    setIsFieldModified(false)
    setNeedRecalculate(false)
    setChangedAmountData(null)
  }

  const handleFieldFocus = (fieldName: keyof EditableData): void => {
    setActiveField(fieldName)
  }

  const handleEditableChange = (
    field: keyof EditableData,
    value: string,
  ): void => {
    setEditableData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const isFieldDisabled = (fieldName: keyof EditableData): boolean => {
    return (
      (activeField !== null && isFieldModified && activeField !== fieldName) ||
      !editableFieldsByStatus[transactionStatus].includes(fieldName)
    )
  }

  const handleSubmit = (executeUpdate: boolean) => {
    if (activeField) {
      executeUpdate === changeMethod.calculation
        ? calculateAmountMounted({
            targetField: activeField,
            newValue: editableData[activeField],
            executeUpdate,
            comment: comment,
            needRecalculate,
          })
        : updateAmountMounted({
            targetField: activeField,
            newValue: editableData[activeField],
            executeUpdate,
            comment: comment,
            needRecalculate,
          })
    }
  }

  return (
    <div>
      {contextHolder}
      <CRow className="mb-3">
        <CCol sm={4}>
          <strong>Status:</strong>{' '}
          {humanTransactionRequestStatus(transactionStatus)}
        </CCol>
      </CRow>
      <CRow className="mb-3">
        <CCol md="6">
          <InitialValuesForm initialData={initialData} />
        </CCol>
        <CCol md="6">
          {calculateAmountLoading ? (
            <div className="h-100 d-flex justify-content-center align-items-center">
              <CSpinner color="primary" />
            </div>
          ) : (
            <>
              <CRow className="mb-3">
                {editableFieldsByStatus[transactionStatus].includes(
                  'initialAmount',
                ) && (
                  <>
                    <CCol md="3">Initial amount</CCol>
                    <CCol>
                      <CFormInput
                        id="initialAmount"
                        value={editableData.initialAmount}
                        onChange={(e) =>
                          handleEditableChange('initialAmount', e.target.value)
                        }
                        onFocus={() => handleFieldFocus('initialAmount')}
                        disabled={isFieldDisabled('initialAmount')}
                      />
                    </CCol>
                  </>
                )}
              </CRow>
              <CRow className="mb-3">
                {editableFieldsByStatus[transactionStatus].includes(
                  'clientAmount',
                ) && (
                  <>
                    <CCol md="3">Client Amount</CCol>
                    <CCol>
                      <CFormInput
                        id="clientAmount"
                        value={editableData.clientAmount}
                        onChange={(e) =>
                          handleEditableChange('clientAmount', e.target.value)
                        }
                        onFocus={() => handleFieldFocus('clientAmount')}
                        disabled={isFieldDisabled('clientAmount')}
                      />
                    </CCol>
                    <CCol>
                      <CFormSelect
                        id="clientCurrency"
                        value={editableData.clientCurrency}
                        onChange={(e) =>
                          handleEditableChange('clientCurrency', e.target.value)
                        }
                        onFocus={() => handleFieldFocus('clientCurrency')}
                        disabled={isFieldDisabled('clientCurrency')}
                      >
                        {currencies.map((item) => (
                          <option value={item} key={item}>
                            {item}
                          </option>
                        ))}
                      </CFormSelect>
                    </CCol>
                  </>
                )}
              </CRow>
              <CRow className="mb-3">
                {editableFieldsByStatus[transactionStatus].includes(
                  'companyAmount',
                ) && (
                  <>
                    <CCol md="3">Company Amount</CCol>
                    <CCol>
                      <CFormInput
                        id="companyAmount"
                        value={editableData.companyAmount}
                        onChange={(e) =>
                          handleEditableChange('companyAmount', e.target.value)
                        }
                        onFocus={() => handleFieldFocus('companyAmount')}
                        disabled={isFieldDisabled('companyAmount')}
                      />
                    </CCol>
                    <CCol>
                      <CFormSelect
                        id="companyCurrency"
                        value={editableData.companyCurrency}
                        disabled={true}
                      >
                        {currencies.map((item) => (
                          <option value={item} key={item}>
                            {item}
                          </option>
                        ))}
                      </CFormSelect>
                    </CCol>
                  </>
                )}
              </CRow>
              <CRow className="mb-3">
                {editableFieldsByStatus[transactionStatus].includes(
                  'innerCommission',
                ) && (
                  <>
                    <CCol md="3">
                      Inner Commission{' '}
                      <Tooltip title="Need to change after other values">
                        <InfoCircleOutlined />
                      </Tooltip>
                    </CCol>
                    <CCol>
                      <CFormInput
                        id="innerCommission"
                        value={editableData.innerCommission}
                        onChange={(e) =>
                          handleEditableChange(
                            'innerCommission',
                            e.target.value,
                          )
                        }
                        onFocus={() => handleFieldFocus('innerCommission')}
                        disabled={isFieldDisabled('innerCommission')}
                      />
                    </CCol>
                  </>
                )}
              </CRow>
              <CRow className="mb-3">
                <CCol md="3">Company Commission:</CCol>
                <CCol>
                  <p>
                    {changedAmountData
                      ? changedAmountData.companyCommission
                      : initialData.companyCommission}
                  </p>
                </CCol>
              </CRow>
              <CRow className="mb-3">
                <CCol md="3">Client Commission:</CCol>
                <CCol>
                  <p>
                    {changedAmountData
                      ? changedAmountData.clientCommission
                      : initialData.clientCommission}
                  </p>
                </CCol>
              </CRow>
              <CRow className="mb-3">
                <CCol md="3">Exchange Rate:</CCol>
                <CCol>
                  <p>
                    {changedAmountData
                      ? changedAmountData.exchangeRate
                      : initialData.exchangeRate}
                  </p>
                </CCol>
              </CRow>
              <CRow className="mb-3">
                <CCol md="3">Initial Amount usd:</CCol>
                <CCol>
                  <p>
                    {changedAmountData
                      ? changedAmountData.initialAmountUsd
                      : initialData.initialAmountUsd}
                  </p>
                </CCol>
              </CRow>
              <CRow className="mb-3">
                <CFormCheck
                  checked={needRecalculate}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setNeedRecalculate(e.target.checked)
                  }
                  disabled={
                    !(
                      activeField === 'initialAmount' ||
                      activeField === 'clientAmount'
                    )
                  }
                  label="Is need recalculate?"
                />
              </CRow>
              <CRow>
                <div className="d-flex gap-2 justify-content-end">
                  <CButton
                    color="primary"
                    onClick={() => handleSubmit(changeMethod.calculation)}
                    disabled={!activeField || !isFieldModified}
                  >
                    Calculate
                  </CButton>
                  <CButton color="secondary" onClick={handleReset}>
                    Reset
                  </CButton>
                </div>
              </CRow>
            </>
          )}
        </CCol>
      </CRow>
      {isDiffAmountAfterChanged && (
        <CRow className="mb-3">
          <div className="alert alert-secondary" role="alert">
            {changedAmountData &&
              Number(initialData.initialAmount) !==
                Number(changedAmountData.initialAmount) && (
                <div>{`Initial amount changed ${initialData.initialAmount} -> ${changedAmountData.initialAmount}`}</div>
              )}
            {changedAmountData &&
              Number(initialData.clientAmount) !==
                Number(changedAmountData.clientAmount) && (
                <div>{`Client amount changed ${initialData.clientAmount} -> ${changedAmountData.clientAmount}`}</div>
              )}
            {changedAmountData &&
              Number(initialData.companyAmount) !==
                Number(changedAmountData.companyAmount) && (
                <div>{`Company amount changed ${initialData.companyAmount} -> ${changedAmountData.companyAmount}`}</div>
              )}
            {changedAmountData &&
              Number(initialData.innerCommission) !==
                Number(changedAmountData.innerCommission) && (
                <div>{`Inner commission changed ${initialData.innerCommission} -> ${changedAmountData.innerCommission}`}</div>
              )}
            {changedAmountData &&
              Number(initialData.clientCommission) !==
                Number(changedAmountData.clientCommission) && (
                <div>{`Client commission changed ${initialData.clientCommission} -> ${changedAmountData.clientCommission}`}</div>
              )}
            {changedAmountData &&
              Number(initialData.companyCommission) !==
                Number(changedAmountData.companyCommission) && (
                <div>{`Company commission changed ${initialData.companyCommission} -> ${changedAmountData.companyCommission}`}</div>
              )}
            {changedAmountData &&
              Number(initialData.initialAmountUsd) !==
                Number(changedAmountData.initialAmountUsd) && (
                <div>{`Initial amount usd changed ${initialData.initialAmountUsd} -> ${changedAmountData.initialAmountUsd}`}</div>
              )}
          </div>
        </CRow>
      )}
      <CRow className="mb-3">
        <CCol>
          <CFormTextarea
            value={comment}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              setComment(e.target.value)
            }
            placeholder="Explain the reason for the changes..."
            required
            label="Internal Comment"
            rows={4}
          />
        </CCol>
      </CRow>
      <CRow>
        <div className="d-flex gap-2 justify-content-end">
          <CButton
            color="primary"
            onClick={() => handleSubmit(changeMethod.update)}
            disabled={!changedAmountData || !comment.trim()}
          >
            {updateAmountLoading ? <CSpinner /> : 'Update amount'}
          </CButton>
        </div>
      </CRow>
    </div>
  )
}
